import { useState, useEffect, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout, Avatar, Drawer, Table, Tag, Statistic, Card, Row, Col, Spin, Typography, Modal, Button, Select } from 'antd';
import {
  ProjectOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  FileDoneOutlined,
  CodeOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  RobotOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  FilePptOutlined,
  PictureOutlined,
  ToolOutlined,
  FolderOpenOutlined,
  ApiOutlined,
  RadarChartOutlined,
  BulbOutlined,
  HighlightOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';
import AmbientCanvas from '../../components/AmbientCanvas';
import './MainLayout.css';

const { Header, Content } = Layout;
const { Text, Title } = Typography;

const menuItems: Array<{
  key: string;
  icon: ReactNode;
  label: string;
  desc: string;
  accent?: boolean;
}> = [
  { key: '/workroom', icon: <ToolOutlined />, label: '智能工坊', desc: '文档/表格/报价' },
  { key: '/', icon: <DashboardOutlined />, label: '工作台', desc: '总览' },
  { key: '/ai-demand-center', icon: <FileTextOutlined />, label: 'AI需求共创', desc: '共创' },
  { key: '/dev-workspace', icon: <CodeOutlined />, label: 'AI全流程', desc: '构建' },
  { key: '/digital-office', icon: <RobotOutlined />, label: '数字员工', desc: '执行' },
  { key: '/agent-runtime', icon: <ApiOutlined />, label: '业务智舱', desc: '经营看板', accent: true },
  { key: '/ai-command-office', icon: <RadarChartOutlined />, label: '指挥办公室', desc: '可视化指挥', accent: true },
];

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [tokenOpen, setTokenOpen] = useState(false);
  const [tokenStats, setTokenStats] = useState<any>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [allUserTokens, setAllUserTokens] = useState<any[]>([]);
  const [adminTokenOpen, setAdminTokenOpen] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [tokenModelFilter, setTokenModelFilter] = useState<string>('all');
  const [myCompOpen, setMyCompOpen] = useState(false);
  const [myComps, setMyComps] = useState<any[]>([]);
  const [compLoading, setCompLoading] = useState(false);
  const isAdmin = user?.roles?.includes('admin') || user?.username === 'admin';

  // 企业版功能列表
  const enterpriseFeatures = ['/digital-office', '/ai-demand-center', '/ppt-designer', '/design-creator', '/skill-market', '/agent-developer-center', '/tech-components'];

  const userFeatures: string[] = isAdmin
    ? enterpriseFeatures
    : (user as any)?.allowedFeatures || [];

  const hasFeature = (key: string) => isAdmin || userFeatures.includes(key);
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);

  useEffect(() => {
    if (tokenOpen) fetchMyTokens();
  }, [tokenOpen]);

  useEffect(() => {
    if (adminTokenOpen && isAdmin) fetchAllTokens();
  }, [adminTokenOpen, isAdmin]);

  const fetchMyTokens = async () => {
    setTokenLoading(true);
    try {
      const r = await api.get('/users/me/tokens', { params: { period: '30d' } });
      setTokenStats(r.data);
    } catch { setTokenStats(null); }
    setTokenLoading(false);
  };

  const fetchAllTokens = async () => {
    setAdminLoading(true);
    try {
      const r = await api.get('/users/tokens/all', { params: { period: '30d' } });
      setAllUserTokens(r.data || []);
    } catch { setAllUserTokens([]); }
    setAdminLoading(false);
  };

  const fetchMyComps = async () => {
    setCompLoading(true);
    try {
      const wbRes = await api.get('/dev-workspace/workbenches');
      const wbs = wbRes.data || [];
      if (wbs.length > 0) {
        const r = await api.get(`/tech-components/user/${wbs[0].id}`);
        setMyComps(r.data || []);
      }
    } catch { setMyComps([]); }
    setCompLoading(false);
  };

  const selectedMenuKey = menuItems.find((item) =>
    item.key === '/' ? location.pathname === '/' : location.pathname.startsWith(item.key)
  )?.key || '/';
  const extensionTriggerLabel = '智能中心';
  const featureTriggerLabel = '功能中心';

  const handleMenuClick = ({ key }: { key: string }) => {
    // 数字员工、AI需求共创与智能中心内的企业功能做权限拦截；基础导航所有用户可访问
    if (enterpriseFeatures.includes(key) && !hasFeature(key)) {
      setEnterpriseModalOpen(true);
      return;
    }
    navigate(key);
  };
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <Layout className="main-layout">
      <AmbientCanvas className="main-ambient-canvas" density={72} animated={!location.pathname.startsWith('/dev-workspace')} />
      <div className="df-orb df-orb-one" />
      <div className="df-orb df-orb-two" />
      <Layout className="app-shell">
        <Header className="header top-command-nav">
          <div className="header-brand" onClick={() => navigate('/')}>
            <div className="brand-mark"><img src="/assets/devforge-logo.png" alt="智坊 DevForge" style={{width:'100%',height:'100%',objectFit:'contain'}} /></div>
            <div className="brand-copy">
              <span className="header-brand-title">智坊 DevForge</span>
              <span className="logo-subtitle">AI AGENT <span style={{color:'#7dd3fc',marginLeft:5,fontSize:10,fontWeight:800}}>v84-0706</span></span>
            </div>
          </div>

          <nav className="nav-card-strip" aria-label="主导航">
            {menuItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`nav-card ${item.accent ? 'nav-card-agent' : ''} ${!item.accent && selectedMenuKey === item.key ? 'is-active' : ''}`}
                onClick={() => handleMenuClick({ key: item.key })}
              >
                <span className="nav-card-glow" />
                <span className="nav-card-icon">{item.icon}</span>
                <span className="nav-card-text">
                  <span className="nav-card-label">{item.label}</span>
                </span>
              </button>
            ))}
          </nav>

          <div className="header-right">
            <div className="command-menu command-menu-features">
              <button type="button" className="command-menu-trigger command-menu-trigger-feature">
                <FolderOpenOutlined />
                <span>{featureTriggerLabel}</span>
              </button>
              <div className="command-flyout command-flyout-features">
                <button type="button" className="flyout-action flyout-blue" onClick={() => navigate('/artifacts')}>
                  <FileDoneOutlined />
                  <span><strong>产物中心</strong><em>交付沉淀</em></span>
                </button>
                <button type="button" className="flyout-action flyout-cyan" onClick={() => navigate('/history')}>
                  <ClockCircleOutlined />
                  <span><strong>历史记录</strong><em>过程回溯</em></span>
                </button>
                <button type="button" className="flyout-action flyout-violet" onClick={() => navigate('/projects')}>
                  <ProjectOutlined />
                  <span><strong>项目管理</strong><em>项目交付</em></span>
                </button>
                <button type="button" className="flyout-action flyout-gold" onClick={() => navigate('/work-intel')}>
                  <RadarChartOutlined />
                  <span><strong>工作情报</strong><em>待办雷达 · AI研判</em></span>
                </button>
                <button type="button" className="flyout-action flyout-cyan" onClick={() => navigate('/idea-center')}>
                  <BulbOutlined />
                  <span><strong>创意中心</strong><em>AI创意 · 智坊灵感</em></span>
                </button>
                <button type="button" className="flyout-action flyout-blue" onClick={() => navigate('/free-whiteboard')}>
                  <HighlightOutlined />
                  <span><strong>自由画板</strong><em>白板 · 手绘 · 头脑风暴</em></span>
                </button>
              </div>
            </div>

            <div className="command-menu command-menu-extensions">
              <button type="button" className="command-menu-trigger command-menu-trigger-smart">
                <AppstoreOutlined />
                <span>{extensionTriggerLabel}</span>
              </button>
              <div className="command-flyout command-flyout-extensions">
                <button type="button" className="flyout-action flyout-gold" onClick={() => navigate('/knowledge')}>
                  <DatabaseOutlined />
                  <span><strong>知识库</strong><em>资料检索增强</em></span>
                </button>
                <button type="button" className="flyout-action flyout-cyan"
                  onClick={() => { if (!hasFeature('/ppt-designer')) { setEnterpriseModalOpen(true); return; } navigate('/ppt-designer'); }}>
                  <FilePptOutlined />
                  <span><strong>AI PPT设计</strong><em>可编辑汇报材料</em></span>
                </button>
                <button type="button" className="flyout-action flyout-blue"
                  onClick={() => { if (!hasFeature('/design-creator')) { setEnterpriseModalOpen(true); return; } navigate('/design-creator'); }}>
                  <PictureOutlined />
                  <span><strong>设计图创作</strong><em>生图与上传改字</em></span>
                </button>
                <button type="button" className="flyout-action flyout-violet"
                  onClick={() => { if (!hasFeature('/skill-market')) { setEnterpriseModalOpen(true); return; } navigate('/skill-market'); }}>
                  <AppstoreOutlined />
                  <span><strong>智件超市</strong><em>能力市场</em></span>
                </button>
                {isAdmin && (
                  <button type="button" className="flyout-action flyout-cyan"
                    onClick={() => { navigate('/agent-developer-center'); }}>
                    <ApiOutlined />
                    <span><strong>开发者中心</strong><em>管理员接入</em></span>
                  </button>
                )}
                <button type="button" className="flyout-action flyout-blue"
                  onClick={() => { if (!hasFeature('/tech-components')) { setEnterpriseModalOpen(true); return; } navigate('/tech-components'); }}>
                  <ToolOutlined />
                  <span><strong>技术组件</strong><em>组件资产</em></span>
                </button>
              </div>
            </div>

            <div className="command-menu command-menu-user">
              <button type="button" className="command-menu-trigger user-menu-trigger">
                <Avatar src={user?.avatarUrl} icon={<UserOutlined />} />
                <span>{user?.name || '我的'}</span>
              </button>
              <div className="command-flyout command-flyout-user">
                <button type="button" className="flyout-action" onClick={() => { setMyCompOpen(true); fetchMyComps(); }}>
                  <span className="flyout-emoji">🧩</span>
                  <span><strong>我的组件</strong><em>查看组件资产</em></span>
                </button>
                <button type="button" className="flyout-action" onClick={() => setTokenOpen(true)}>
                  <ThunderboltOutlined />
                  <span><strong>Token 用量</strong><em>查看消耗统计</em></span>
                </button>
                <button type="button" className="flyout-action" onClick={() => navigate('/settings')}>
                  <SettingOutlined />
                  <span><strong>系统设置</strong><em>模型与平台配置</em></span>
                </button>
                {isAdmin && (
                  <button type="button" className="flyout-action" onClick={() => setAdminTokenOpen(true)}>
                    <DollarOutlined />
                    <span><strong>全员 Token</strong><em>管理员统计</em></span>
                  </button>
                )}
                <button type="button" className="flyout-action flyout-danger" onClick={handleLogout}>
                  <LogoutOutlined />
                  <span><strong>退出登录</strong><em>结束当前会话</em></span>
                </button>
              </div>
            </div>
          </div>
        </Header>
        <Content className="content"><Outlet /></Content>
      </Layout>

      {/* 个人Token用量抽屉 */}
      <Drawer title={<><ThunderboltOutlined style={{ color: '#fa8c16' }} /> Token用量统计（近{tokenStats?.period || '30天'}）</>}
        open={tokenOpen} onClose={() => { setTokenOpen(false); setTokenModelFilter('all'); }} width={760}
        className="token-stats-drawer" rootClassName="token-stats-drawer-root"
        styles={{ body: { background: '#f8fafc', color: '#1e293b', padding: 16 } }}>
        {tokenLoading ? <Spin style={{ display: 'block', margin: '60px auto' }} /> : tokenStats ? (
          <>
            {/* 模型筛选下拉 */}
            {tokenStats.byModel && tokenStats.byModel.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Select value={tokenModelFilter} onChange={setTokenModelFilter} style={{ width: 240 }}
                  options={[
                    { label: '全部模型', value: 'all' },
                    ...tokenStats.byModel.map((m: any) => ({ label: m.model, value: m.model })),
                  ]} />
              </div>
            )}

            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Card size="small"><Statistic title="总Token" value={
                  tokenModelFilter === 'all'
                    ? formatTokens(tokenStats.summary?.totalTokens ?? tokenStats.totalTokens ?? 0)
                    : formatTokens((tokenStats.byModel?.find((m: any) => m.model === tokenModelFilter)?.tokens ?? 0) as number)
                } /></Card>
              </Col>
              <Col span={8}>
                <Card size="small"><Statistic title="调用次数" value={
                  tokenModelFilter === 'all'
                    ? (tokenStats.summary?.totalCalls ?? tokenStats.requestCount ?? 0)
                    : (tokenStats.byModel?.find((m: any) => m.model === tokenModelFilter)?.calls ?? 0)
                } /></Card>
              </Col>
              <Col span={8}>
                <Card size="small"><Statistic title="数据来源" value={tokenStats.summary?.source === 'business_history' ? '历史记录' : '实时'} valueStyle={{fontSize:13}} /></Card>
              </Col>
            </Row>

            {/* 按模型汇总 */}
            {tokenStats.byModel && tokenStats.byModel.length > 0 && (
              <>
                <Text strong style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>📊 按模型汇总</Text>
                <Table size="small"
                  dataSource={tokenModelFilter === 'all' ? tokenStats.byModel : tokenStats.byModel.filter((m: any) => m.model === tokenModelFilter)}
                  rowKey="model" pagination={false} style={{ marginBottom: 16 }}
                  columns={[
                    { title: '模型', dataIndex: 'model', render: (v: string) => <Tag color="blue">{v}</Tag> },
                    { title: 'Token', dataIndex: 'tokens', render: (v: number) => formatTokens(v), sorter: (a: any, b: any) => a.tokens - b.tokens, defaultSortOrder: 'descend' as const },
                    { title: '调用次数', dataIndex: 'calls', sorter: (a: any, b: any) => a.calls - b.calls },
                  ]} />
              </>
            )}

            {/* 每日用量（按天×模型） */}
            {tokenStats.daily && tokenStats.daily.length > 0 && (
              <>
                <Text strong style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>📅 每日用量</Text>
                <Table size="small"
                  dataSource={tokenModelFilter === 'all' ? tokenStats.daily : tokenStats.daily.filter((r: any) => r.model === tokenModelFilter)}
                  rowKey={(r: any) => r.date + r.model} pagination={{ pageSize: 15 }} style={{ marginBottom: 16 }}
                  columns={[
                    { title: '日期', dataIndex: 'date', width: 80 },
                    { title: '模型', dataIndex: 'model', width: 160, render: (v: string) => <Tag color="blue">{v}</Tag> },
                    { title: '输入', dataIndex: 'input', render: (v: number) => formatTokens(v), width: 90 },
                    { title: '输出', dataIndex: 'output', render: (v: number) => formatTokens(v), width: 90 },
                    { title: '合计', render: (_: any, r: any) => <Text strong>{formatTokens((r.input||0) + (r.output||0))}</Text>, width: 90 },
                    { title: '调用', dataIndex: 'calls', width: 60 },
                  ]} />
              </>
            )}

            {/* 按小时用量（仅1d） */}
            {tokenStats.hourly && tokenStats.hourly.length > 0 && (
              <>
                <Text strong style={{ fontSize: 14, marginBottom: 8, display: 'block' }}>⏰ 按小时用量</Text>
                <Table size="small"
                  dataSource={tokenModelFilter === 'all' ? tokenStats.hourly : tokenStats.hourly.filter((r: any) => r.model === tokenModelFilter)}
                  rowKey={(r: any) => r.hour + r.model} pagination={false}
                  columns={[
                    { title: '小时', dataIndex: 'hour', width: 80 },
                    { title: '模型', dataIndex: 'model', width: 160, render: (v: string) => <Tag color="geekblue">{v}</Tag> },
                    { title: '输入', dataIndex: 'input', render: (v: number) => formatTokens(v), width: 90 },
                    { title: '输出', dataIndex: 'output', render: (v: number) => formatTokens(v), width: 90 },
                    { title: '合计', render: (_: any, r: any) => <Text strong>{formatTokens((r.input||0) + (r.output||0))}</Text>, width: 90 },
                    { title: '调用', dataIndex: 'calls', width: 60 },
                  ]} />
              </>
            )}
          </>
        ) : <Text type="secondary">暂无数据</Text>}
      </Drawer>

      {/* 管理员：全员Token统计 */}
      {isAdmin && (
        <Drawer title={<><DollarOutlined style={{ color: '#52c41a' }} /> 全员Token用量（近30天）</>}
          open={adminTokenOpen} onClose={() => setAdminTokenOpen(false)} width={700}>
          {adminLoading ? <Spin style={{ display: 'block', margin: '60px auto' }} /> : allUserTokens.length > 0 ? (
            <Table size="small" dataSource={allUserTokens} rowKey="userId" pagination={false}
              columns={[
                { title: '用户', dataIndex: 'name', render: (v: string, r: any) => <>{v} <Tag>{r.username}</Tag></>, width: 140 },
                { title: '总Token', dataIndex: ['summary', 'totalTokens'], render: (_: any, r: any) => <Text strong>{formatTokens(r.summary?.totalTokens ?? r.totalTokens ?? 0)}</Text>, sorter: (a: any, b: any) => (a.summary?.totalTokens ?? a.totalTokens ?? 0) - (b.summary?.totalTokens ?? b.totalTokens ?? 0), defaultSortOrder: 'descend' as const },
                { title: '调用', render: (_: any, r: any) => r.summary?.totalCalls ?? r.requestCount ?? 0 },
                { title: '来源', render: (_: any, r: any) => <Tag>{r.summary?.source === 'business_history' ? '历史' : '实时'}</Tag>, width: 60 },
              ]} />
          ) : <Text type="secondary">暂无数据</Text>}
        </Drawer>
      )}
      {/* 企业版功能提示弹窗 */}
      <Modal
        title="企业版功能"
        open={enterpriseModalOpen}
        onCancel={() => setEnterpriseModalOpen(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setEnterpriseModalOpen(false)}>我知道了</Button>,
        ]}
        width={420}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <AppstoreOutlined style={{ fontSize: 48, color: '#7c3aed', marginBottom: 16 }} />
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#333' }}>
            当前功能未开通使用权限
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            请联系管理员在工作台中开启对应功能权限
          </div>
        </div>
      </Modal>
    </Layout>
  );
}

